import React, { createContext, useContext, useMemo } from 'react';
import type {
  CurrentUser as CurrentUserPayload,
  UserPreferences,
  UserPreferencesPatch,
  UserRole,
} from '@techgitter/contracts';
import { updateMyPreferences } from '../services/meApi';

/** What a user who has never saved a preference gets — matches the API's defaults. */
export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  showDebugPanel: false,
  defaultLabelPrinter: null,
};

/**
 * Identity + roles for the signed-in user, sourced from `/api/me`.
 *
 * Replaces the legacy `userInfo` prop drilled through MainLayout. Components
 * should call `useCurrentUser()` rather than reading `userInfo.roles` so the
 * role values stay in sync with the canonical contract enum (PascalCase).
 *
 * The `userInfo` prop is still passed through MainLayout during the migration
 * for back-compat with consumers that haven't yet been switched over — those
 * sites use lowercase role strings and should be migrated to `hasRole(...)`
 * over time. See docs/architecture/roles-and-authorization.md in
 * sm4rt-api for the full rollout plan.
 */
export interface CurrentUser extends CurrentUserPayload {
  /** Always complete — defaults filled in when the API sent none. */
  preferences: UserPreferences;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (...roles: UserRole[]) => boolean;
  /**
   * Save some of the signed-in user's own preferences. Resolves to the full
   * saved set, which is also pushed into this context so every reader (the
   * debug panel, the Print page) updates without a reload. Rejects on failure,
   * leaving the context unchanged.
   */
  updatePreferences: (patch: UserPreferencesPatch) => Promise<UserPreferences>;
}

const CurrentUserContext = createContext<CurrentUser | null>(null);

interface CurrentUserProviderProps {
  value: CurrentUserPayload | null;
  /** Called with the saved preferences so the owner of `value` can store them. */
  onPreferencesChange?: (preferences: UserPreferences) => void;
  children: React.ReactNode;
}

export const CurrentUserProvider: React.FC<CurrentUserProviderProps> = ({
  value,
  onPreferencesChange,
  children,
}) => {
  const contextValue = useMemo<CurrentUser | null>(() => {
    if (!value) return null;
    const roleSet = new Set(value.roles);
    return {
      ...value,
      preferences: { ...DEFAULT_USER_PREFERENCES, ...value.preferences },
      hasRole: (role) => roleSet.has(role),
      hasAnyRole: (...roles) => roles.some((r) => roleSet.has(r)),
      updatePreferences: async (patch) => {
        const saved = await updateMyPreferences(patch);
        onPreferencesChange?.(saved);
        return saved;
      },
    };
  }, [value, onPreferencesChange]);

  return (
    <CurrentUserContext.Provider value={contextValue}>
      {children}
    </CurrentUserContext.Provider>
  );
};

/**
 * Returns the resolved current user, or `null` while `/api/me` is in flight or
 * if the user is not provisioned. Components that should never render before
 * the user is known should be mounted inside an authorization gate that waits
 * for non-null.
 */
export const useCurrentUser = (): CurrentUser | null => useContext(CurrentUserContext);

/**
 * Same as useCurrentUser but throws when the value is null. Use this in
 * components that you know are mounted only after `/api/me` resolves.
 */
export const useRequiredCurrentUser = (): CurrentUser => {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) {
    throw new Error('useRequiredCurrentUser called outside an authenticated tree');
  }
  return ctx;
};
